import { IMAGE_ACCEPT, useFilesOnDrop } from "@/lib/messages";
import { classNames } from "@/lib/utils";
import LoadingIndicator from "@components/library/LoadingIndicator";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

interface Props {
  onDrop: (dataUrls: string[]) => void;
  multiple?: boolean;
}

export const UploadImageDropzoneWrapper = ({ onDrop, multiple, children }: React.PropsWithChildren<Props>) => {
  const [dropped, setDropped] = useState(false);

  // Convert files to data URLs.
  const _filesOnDrop = useFilesOnDrop(onDrop);

  // Sets `dropped` to show loading states
  const filesOnDrop = useCallback(
    async (files: File[]) => {
      setDropped(true);
      await _filesOnDrop(files);
      setDropped(false);
    },
    [_filesOnDrop],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    multiple,
    accept: IMAGE_ACCEPT,
    onDrop: filesOnDrop,
    noClick: true,
    noDragEventsBubbling: true, // Allow nested dropzones to not trigger parent dropzones.
  });
  const state = isDragAccept ? "accept" : isDragReject ? "reject" : "none";

  return (
    <div {...getRootProps()} className="relative">
      <input {...getInputProps()} />
      {children}
      <div
        className={classNames(
          "pointer-events-none absolute inset-4 flex items-center justify-center rounded border-2 border-dashed bg-white/90 backdrop-blur-[0.5px]",
          `opacity-0 transition-opacity`,
          state === "accept" && "!opacity-100",
        )}
      >
        <div className="text-14-16 text-text-base-2">Drop image to upload</div>
      </div>
      {dropped && (
        <div className="absolute inset-4 flex items-center justify-center rounded border-2 border-dashed bg-white/90 backdrop-blur-[0.5px]">
          <LoadingIndicator className="h-36 w-36" />
        </div>
      )}
    </div>
  );
};
